import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { leaveApi } from "../../services/leaveApi";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function MyLeaveRequestsPage() {
  const { t } = useLocalization();
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ start_date: "", end_date: "", days_requested: "", reason: "" });
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = () => leaveApi.myRequests().then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.start_date || !form.end_date || !form.days_requested) { toast.error(t("myLeaveRequests.requiredFieldsError")); return; }
    try {
      await leaveApi.submitMyRequest({ ...form, days_requested: Number(form.days_requested) });
      toast.success(t("myLeaveRequests.requestSubmitted"));
      setForm({ start_date: "", end_date: "", days_requested: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="my-leave-requests-page">
      <PageHelpBanner pageKey="my-leave-requests" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.myLeaveRequests")}</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("myLeaveRequests.title")}</h2>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="submit-leave-request-form">
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="request-start-date-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Input data-testid="request-end-date-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <Input data-testid="request-days-input" type="number" value={form.days_requested} onChange={(e) => setForm({ ...form, days_requested: e.target.value })} placeholder={t("myLeaveRequests.daysRequestedPlaceholder")} />
        <Input data-testid="request-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("myLeaveRequests.reasonPlaceholder")} />
        <Button data-testid="submit-leave-request-btn" onClick={submit} className="w-full">{t("myLeaveRequests.submitRequest")}</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {requests.map((r) => (
          <div key={r.id} className="p-4 flex items-center justify-between" data-testid={`my-leave-request-row-${r.id}`}>
            <div>
              <p className="text-sm font-semibold text-gray-800">{r.start_date} → {r.end_date} ({r.days_requested} {t("myLeaveRequests.daysUnit")})</p>
              <p className="text-xs text-gray-500">{r.reason || t("myLeaveRequests.noReasonGiven")}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              {(r.status === "PENDING" || r.status === "APPROVED") && (
                <button data-testid={`my-cancel-request-btn-${r.id}`} onClick={() => setCancelTarget(r)} className="text-xs font-semibold text-red-600 hover:underline">{t("myLeaveRequests.cancel")}</button>
              )}
            </div>
          </div>
        ))}
        {requests.length === 0 && <p className="p-6 text-center text-sm text-gray-500" data-testid="no-my-leave-requests">{t("myLeaveRequests.noRequests")}</p>}
      </div>

      {cancelTarget && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setCancelTarget(null)} title={t("myLeaveRequests.cancelDialogTitle")}
          contextItems={[
            { label: t("myLeaveRequests.cancelDialogDates"), value: `${cancelTarget.start_date} → ${cancelTarget.end_date}` },
            { label: t("myLeaveRequests.cancelDialogDays"), value: cancelTarget.days_requested },
            { label: t("myLeaveRequests.cancelDialogStatus"), value: t(`statuses.${cancelTarget.status}`) },
          ]}
          reasonLabel={t("myLeaveRequests.cancelReasonLabel")} reasonPlaceholder={t("myLeaveRequests.cancelReasonPlaceholder")}
          cancelLabel={t("common.cancel")} danger confirmLabel={t("myLeaveRequests.cancelConfirmLabel")}
          onConfirm={async (reason) => { await leaveApi.cancelMyRequest(cancelTarget.id, reason); toast.success(t("myLeaveRequests.requestCancelled")); load(); }}
          testId="my-cancel-request-dialog"
        />
      )}
    </div>
  );
}

